import type {
  BonusCombineMode,
  ConsentChannel,
  DataRegion,
  EnrolmentMode,
  Industry,
  MemberIdType,
  NegativeBalancePolicy,
  PerkProgram,
  PersonalDataField,
  PointsExpiryMode,
  PointsRounding,
  ProgramEventType,
  ProgramStatus,
  ProgramTimeZone,
  SupportedLanguage,
} from "@/pages/perkpoint/programs/mock-programs";
import { perkpointApi, type ApiPage } from "./client";

/* ------------------------------------------------------------------ */
/* API shapes (snake_case, as returned by api/)                        */
/* ------------------------------------------------------------------ */

export type ApiProgramContact = {
  id: string;
  name: string;
  email: string;
  role: string;
  country_code: string | null;
  phone_number: string | null;
};

export type ApiProgramTier = {
  id: string;
  key: string;
  name: string;
  rank: number;
  invite_only: boolean;
  display_color: string | null;
  entry_points: number | null;
  members: number;
  is_starting: boolean;
};

export type ApiProgram = {
  id: string;
  name: string;
  slug: string;
  status: "draft" | "active" | "paused" | "closed";
  description: string | null;
  program_type: string | null;
  created_at: string;
  launched_at: string | null;
  currency: string;
  time_zone: string;
  data_region: string;
  supported_languages: string[];
  point_value: number | string;
  point_name: string | null;
  industry: string;
  program_owner: string | null;
  brand_primary_color?: string | null;
  client_legal_name: string | null;
  billing_contact_name: string | null;
  billing_contact_email: string | null;
  member_terms_url: string | null;
  member_terms_version?: string | null;
  privacy_policy_url: string | null;
  privacy_policy_version?: string | null;
  program_logo_uri?: string | null;
  tax_id?: string | null;
  website_url: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  invoice_frequency_in_days: number | null;
  percentage_of_cashback_to_be_accrued?: number | null;
  offer_denomination: number | null;
  txn_commission_pct_charged_to_prg?: number | null;
  points_rounding: string;
  points_hold_days: number | null;
  cancellation_period_days: number | null;
  points_expiry_mode: "never" | "months";
  default_points_expiry_months: number | null;
  negative_balance_policy: string;
  bonus_combine_mode: string;
  enrolment_mode: string;
  member_id_type: string;
  personal_data_collected: string[];
  consent_channels: string[];
  data_retention_months: number;
  erasure_policy: string;
  enabled_event_types: string[];
  custom_attributes: string[];
  webhook_url?: string | null;
  messaging_enabled: boolean;
  messaging_whatsapp_number?: string | null;
  messaging_sms_sender_id?: string | null;
  messaging_from_email?: string | null;
  quiet_hours_start: string;
  quiet_hours_end: string;
  freq_cap_messages: number;
  campaign_approval_threshold_points: number;
  adjustment_approval_threshold: number;
  support_agent_adjustment_cap: number;
  members_count: number;
  points_issued: number;
  // Detail-only fields
  launched?: boolean;
  launch_gaps?: string[];
  contacts?: ApiProgramContact[];
  tiers?: ApiProgramTier[];
  assigned_tiers?: string[];
  starting_tier?: string | null;
  pending_economy_change?: {
    id: string;
    changes: Record<string, unknown>;
    effective_at: string;
    created_at: string;
  } | null;
};

export type ProgramStats = {
  total: number;
  active: number;
  draft: number;
  paused: number;
  closed: number;
  linked_members: number;
};

export type ProgramListQuery = {
  search?: string;
  searchBy?: "name" | "code";
  status?: string;
  createdWithin?: "7" | "30" | "90";
  page?: number;
  pageSize?: number;
};

/** A program as the UI uses it, plus API-only extras. */
export type ProgramView = PerkProgram & {
  launchGaps: string[];
  launched: boolean;
  tiers: ApiProgramTier[];
  pendingEconomyChange: ApiProgram["pending_economy_change"];
};

/* ------------------------------------------------------------------ */
/* Mapping                                                             */
/* ------------------------------------------------------------------ */

const LAUNCH_GAP_LABELS: Record<string, string> = {
  member_terms_url: "Member terms URL",
  privacy_policy_url: "Privacy policy URL",
  program_owner: "Program owner",
  owner_contact: "Owner contact",
  admin_contact: "Admin contact",
};

function capitalize(value: string) {
  return value ? value[0].toUpperCase() + value.slice(1).toLowerCase() : value;
}

export function statusFromApi(status: ApiProgram["status"]): ProgramStatus {
  return capitalize(status) as ProgramStatus;
}

export function compactNumber(value: number): string {
  if (value >= 1e6) return `${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return String(value);
}

function hhmm(value: string | null | undefined) {
  return (value ?? "").slice(0, 5);
}

export function programFromApi(api: ApiProgram): ProgramView {
  const created = new Date(api.created_at);
  return {
    id: api.id,
    created: created.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    createdAt: created.getTime(),
    programLogoUri: api.program_logo_uri || null,
    programCode: api.slug,
    name: api.name,
    description: api.description ?? "",
    programType: (api.program_type ?? "earn").toUpperCase(),
    status: statusFromApi(api.status),
    address: api.address ?? "",
    currency: api.currency.trim(),
    costPerPoint: Number(api.point_value) || 0,
    invoiceFrequencyInDays: api.invoice_frequency_in_days ?? 30,
    city: api.city ?? "",
    country: api.country ?? "",
    websiteUrl: api.website_url ?? "",
    domain: api.slug,
    clientName: api.client_legal_name ?? "",
    vatNumber: api.tax_id ?? "",
    loyaltyPointsName: api.point_name ?? "",
    cashbackPercentage: Number(api.percentage_of_cashback_to_be_accrued) || 0,
    offerDenomination: Number(api.offer_denomination) || 1,
    txnCommissionPct: Number(api.txn_commission_pct_charged_to_prg) || 0,
    membersCount: api.members_count ?? 0,
    pointsIssued: compactNumber(api.points_issued ?? 0),
    contacts: (api.contacts ?? []).map((contact) => ({
      id: contact.id,
      name: contact.name,
      email: contact.email,
      role: capitalize(contact.role),
      countryCode: contact.country_code ?? "",
      phoneNumber: contact.phone_number ?? "",
    })),
    linkedMemberIds: [],
    assignedTiers: api.assigned_tiers ?? [],
    startingTier: api.starting_tier ?? "",
    industry: api.industry as Industry,
    programOwner: api.program_owner ?? "",
    brandPrimaryColor: api.brand_primary_color ?? "",
    billingContactName: api.billing_contact_name ?? "",
    billingContactEmail: api.billing_contact_email ?? "",
    memberTermsUrl: api.member_terms_url ?? "",
    memberTermsVersion: api.member_terms_version ?? "",
    privacyPolicyUrl: api.privacy_policy_url ?? "",
    privacyPolicyVersion: api.privacy_policy_version ?? "",
    timeZone: api.time_zone as ProgramTimeZone,
    dataRegion: (api.data_region.toLowerCase() === "india" ? "India" : "UAE") as DataRegion,
    supportedLanguages: api.supported_languages as SupportedLanguage[],
    pointsRounding: api.points_rounding as PointsRounding,
    pointsHoldDays: api.points_hold_days,
    cancellationPeriodDays: api.cancellation_period_days,
    pointsExpiryMode: api.points_expiry_mode as PointsExpiryMode,
    pointsExpiryMonths: api.default_points_expiry_months,
    negativeBalancePolicy: (api.negative_balance_policy === "clawback"
      ? "recover"
      : "allow") as NegativeBalancePolicy,
    bonusCombineMode: api.bonus_combine_mode as BonusCombineMode,
    economyEffectiveDate: api.pending_economy_change?.effective_at?.slice(0, 10) ?? null,
    economyNoticeAcknowledged: Boolean(api.pending_economy_change),
    enrolmentMode: api.enrolment_mode as EnrolmentMode,
    memberIdType: api.member_id_type as MemberIdType,
    personalDataCollected: api.personal_data_collected as PersonalDataField[],
    consentChannels: api.consent_channels as ConsentChannel[],
    dataRetentionMonths: api.data_retention_months,
    erasurePolicy: api.erasure_policy ?? "",
    enabledEventTypes: api.enabled_event_types as ProgramEventType[],
    customAttributes: api.custom_attributes ?? [],
    webhookUrl: api.webhook_url ?? "",
    messagingEnabled: api.messaging_enabled,
    messagingWhatsappNumber: api.messaging_whatsapp_number ?? "",
    messagingSmsSenderId: api.messaging_sms_sender_id ?? "",
    messagingFromEmail: api.messaging_from_email ?? "",
    quietHoursStart: hhmm(api.quiet_hours_start),
    quietHoursEnd: hhmm(api.quiet_hours_end),
    messageFrequencyCap: api.freq_cap_messages,
    offerApprovalThreshold: api.campaign_approval_threshold_points,
    adjustmentApprovalThreshold: api.adjustment_approval_threshold,
    supportAgentAdjustmentCap: api.support_agent_adjustment_cap,
    // List rows may not carry launch_gaps yet; fall back to the field checks
    // we can see (contacts are only checked when the API sends the gaps).
    launchGaps: (
      api.launch_gaps ??
      (api.status === "draft"
        ? [
            !api.member_terms_url ? "member_terms_url" : "",
            !api.privacy_policy_url ? "privacy_policy_url" : "",
            !api.program_owner ? "program_owner" : "",
          ].filter(Boolean)
        : [])
    ).map((gap) => LAUNCH_GAP_LABELS[gap] ?? gap),
    launched: api.launched ?? (api.status === "active" || api.status === "paused"),
    tiers: api.tiers ?? [],
    pendingEconomyChange: api.pending_economy_change ?? null,
  };
}

/**
 * Full form payload for POST / PATCH. Field names follow the create form's
 * contract in api/API.md; the API ignores values that did not change.
 */
export function programToPayload(
  program: PerkProgram,
  options: { includeTiers?: boolean } = {},
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: program.name,
    slug: program.programCode,
    program_code: program.programCode,
    description: program.description,
    program_type: program.programType.toLowerCase(),
    status: program.status.toLowerCase(),
    currency: program.currency,
    time_zone: program.timeZone,
    data_region: program.dataRegion === "UAE" ? "uae" : "india",
    point_value: program.costPerPoint,
    cost_per_point: program.costPerPoint,
    point_name: program.loyaltyPointsName,
    invoice_frequency_in_days: program.invoiceFrequencyInDays,
    percentage_of_cashback_to_be_accrued: program.cashbackPercentage,
    tax_id: program.vatNumber,
    address: program.address,
    website_url: program.websiteUrl,
    country: program.country,
    city: program.city,
    offer_denomination: program.offerDenomination,
    txn_commission_pct_charged_to_prg: program.txnCommissionPct,
    industry: program.industry,
    program_owner: program.programOwner,
    brand_primary_color: program.brandPrimaryColor || undefined,
    client_legal_name: program.clientName,
    billing_contact_name: program.billingContactName,
    billing_contact_email: program.billingContactEmail,
    member_terms_url: program.memberTermsUrl,
    member_terms_version: program.memberTermsVersion,
    privacy_policy_url: program.privacyPolicyUrl,
    privacy_policy_version: program.privacyPolicyVersion,
    program_logo_uri: program.programLogoUri ?? "",
    supported_languages: program.supportedLanguages,
    points_rounding: program.pointsRounding,
    points_hold_days: program.pointsHoldDays,
    cancellation_period_days: program.cancellationPeriodDays,
    points_expiry_mode: program.pointsExpiryMode,
    points_expiry_months: program.pointsExpiryMonths,
    negative_balance_policy: program.negativeBalancePolicy,
    bonus_combine_mode: program.bonusCombineMode,
    enrolment_mode: program.enrolmentMode,
    member_id_type: program.memberIdType,
    personal_data_collected: program.personalDataCollected,
    consent_channels: program.consentChannels,
    data_retention_months: program.dataRetentionMonths,
    erasure_policy: program.erasurePolicy,
    enabled_event_types: program.enabledEventTypes,
    custom_attributes: program.customAttributes,
    webhook_url: program.webhookUrl,
    messaging_enabled: program.messagingEnabled,
    messaging_whatsapp_number: program.messagingWhatsappNumber,
    messaging_sms_sender_id: program.messagingSmsSenderId,
    messaging_from_email: program.messagingFromEmail,
    quiet_hours_start: program.quietHoursStart,
    quiet_hours_end: program.quietHoursEnd,
    message_frequency_cap: program.messageFrequencyCap,
    offer_approval_threshold: program.offerApprovalThreshold,
    adjustment_approval_threshold: program.adjustmentApprovalThreshold,
    support_agent_adjustment_cap: program.supportAgentAdjustmentCap,
    economy_effective_date: program.economyEffectiveDate,
    economy_notice_acknowledged: program.economyNoticeAcknowledged,
  };
  if (options.includeTiers) {
    payload.assigned_tiers = program.assignedTiers;
    payload.starting_tier = program.startingTier;
  }
  return payload;
}

/** API field → the UI form field that shows it, for scrolling to errors. */
export const PROGRAM_FIELD_FROM_API: Record<string, string> = {
  name: "name",
  slug: "code",
  program_code: "code",
  client_legal_name: "clientName",
  billing_contact_name: "billingContactName",
  billing_contact_email: "billingContactEmail",
  tax_id: "vatNumber",
  website_url: "websiteUrl",
  program_logo_uri: "programLogoUri",
  industry: "industry",
  program_owner: "programOwner",
  brand_primary_color: "brandPrimaryColor",
  member_terms_url: "memberTermsUrl",
  privacy_policy_url: "privacyPolicyUrl",
  point_name: "loyaltyPointsName",
  point_value: "costPerPoint",
  cost_per_point: "costPerPoint",
  points_rounding: "pointsRounding",
  points_hold_days: "pointsHoldDays",
  cancellation_period_days: "cancellationPeriodDays",
  points_expiry_months: "pointsExpiryMonths",
  currency: "paymentCurrency",
  invoice_frequency_in_days: "invoiceFrequency",
  time_zone: "timeZone",
  data_region: "dataRegion",
  supported_languages: "supportedLanguages",
  address: "address",
  country: "country",
  city: "city",
  enrolment_mode: "enrolmentMode",
  member_id_type: "memberIdType",
  personal_data_collected: "personalDataCollected",
  consent_channels: "consentChannels",
  data_retention_months: "dataRetentionMonths",
  erasure_policy: "erasurePolicy",
  enabled_event_types: "enabledEventTypes",
  custom_attributes: "customAttributes",
  webhook_url: "webhookUrl",
  messaging_whatsapp_number: "messagingWhatsappNumber",
  messaging_sms_sender_id: "messagingSmsSenderId",
  messaging_from_email: "messagingFromEmail",
  quiet_hours_start: "quietHoursStart",
  quiet_hours_end: "quietHoursEnd",
  message_frequency_cap: "messageFrequencyCap",
  offer_approval_threshold: "offerApprovalThreshold",
  adjustment_approval_threshold: "adjustmentApprovalThreshold",
  support_agent_adjustment_cap: "supportAgentAdjustmentCap",
  economy_effective_date: "economyEffectiveDate",
  economy_notice_acknowledged: "economyNoticeAcknowledged",
  contacts: "contacts",
};

/* ------------------------------------------------------------------ */
/* Calls                                                               */
/* ------------------------------------------------------------------ */

export const programKeys = {
  all: ["perkpoint", "programs"] as const,
  list: (query: ProgramListQuery) => ["perkpoint", "programs", "list", query] as const,
  detail: (id: string) => ["perkpoint", "programs", "detail", id] as const,
};

export const ProgramsApi = {
  async list(query: ProgramListQuery = {}) {
    const { data } = await perkpointApi.get<ApiPage<ApiProgram> & { stats: ProgramStats }>(
      "/admin/programs",
      { params: query },
    );
    return { ...data, items: data.items.map(programFromApi) };
  },
  async get(id: string) {
    const { data } = await perkpointApi.get<ApiProgram>(`/admin/programs/${id}`);
    return programFromApi(data);
  },
  async create(payload: Record<string, unknown>) {
    const { data } = await perkpointApi.post<ApiProgram>("/admin/programs", payload);
    return programFromApi(data);
  },
  async update(id: string, payload: Record<string, unknown>) {
    const { data } = await perkpointApi.patch<ApiProgram>(`/admin/programs/${id}`, payload);
    return programFromApi(data);
  },
  async remove(id: string) {
    await perkpointApi.delete(`/admin/programs/${id}`);
  },
  async cancelEconomyChange(id: string) {
    const { data } = await perkpointApi.delete<ApiProgram>(`/admin/programs/${id}/economy-change`);
    return programFromApi(data);
  },
  async addContact(
    id: string,
    contact: { name: string; email: string; role: string; countryCode: string; phoneNumber: string },
  ) {
    await perkpointApi.post(`/admin/programs/${id}/contacts`, {
      name: contact.name,
      email: contact.email,
      role: contact.role.toLowerCase(),
      country_code: contact.countryCode,
      phone_number: contact.phoneNumber,
    });
  },
  async removeContact(id: string, contactId: string) {
    await perkpointApi.delete(`/admin/programs/${id}/contacts/${contactId}`);
  },
};
